import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Typography,
} from "@mui/material";
import { requestDownloadPasswordReset } from "./utils";

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [emailError, setEmailError] = useState("");
  const [serverMessage, setServerMessage] = useState("");

  const validate = () => {
    if (!email.trim()) {
      setEmailError("Please enter your email address");
      return false;
    }
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setEmailError("Please enter a valid email address");
      return false;
    }
    setEmailError("");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);
      setError(null);
      const data = await requestDownloadPasswordReset(email.trim());
      setServerMessage(data?.message || data?.data?.message || "");
      setSuccess(true);
    } catch (err) {
      setError(err.message || "An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
          p: 2,
        }}
      >
        <Card sx={{ borderRadius: 3, maxWidth: 480, width: "100%" }}>
          <CardContent>
            <Typography variant="body1" align="center" sx={{ mb: 1 }}>
              A password reset link has been sent to <strong>{email}</strong>
            </Typography>
            {serverMessage && (
              <Typography
                variant="body2"
                align="center"
                sx={{ mb: 2, color: "text.secondary" }}
              >
                {serverMessage}
              </Typography>
            )}
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Link to="/About/UserLogin" style={{ textDecoration: "none" }}>
                <Button variant="contained" size="small">
                  Back to Login
                </Button>
              </Link>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: 400,
        p: 2,
      }}
    >
      <Card sx={{ borderRadius: 3, maxWidth: 480, width: "100%" }}>
        <CardHeader
          title={<font className="layer-title">Forgot Password</font>}
          align="center"
          className="styledtablerow"
        />
        <CardContent>
          <Typography
            variant="body2"
            align="center"
            sx={{ mb: 3, color: "text.secondary" }}
          >
            Enter the email address associated with your user account to receive
            a password reset link.
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ "& .MuiTextField-root": { width: "100%" } }}
          >
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ mb: 3 }}>
              <TextField
                required
                size="small"
                id="forgot-password-email"
                label="Email Address"
                type="email"
                name="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError("");
                }}
                error={!!emailError}
                helperText={emailError}
                autoComplete="email"
                fullWidth
                disabled={loading}
              />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <Button
                type="submit"
                variant="contained"
                size="small"
                disabled={loading}
                sx={{ minWidth: 160 }}
              >
                {loading ? "Sending" : "Send Reset Link"}
              </Button>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Link to="/About/UserLogin" className="hyper-links">
                Back to Login
              </Link>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ForgotPasswordPage;
